import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { useAuth } from '../contexts/AuthContext';
import { authUrl } from '../utils/authReturn';
import { notepadArticles } from '../data/notepadData';
import './Notepad.css';

function formatDate(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// A blog article's access: 'public' (anyone), 'member' (any account), or
// 'private' (admins only). Unmarked articles default to private.
function canViewArticle(article, user) {
  const level = article.access || 'private';
  if (level === 'public') return true;
  if (!user) return false;
  if (level === 'member') return true;
  return !!user.is_admin;
}

function NotepadList() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Brookline Notepad';
  }, []);

  const sorted = [...notepadArticles]
    .filter(article => canViewArticle(article, user))
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="notepad-page">
      <a href="#main-content" className="notepad-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" subtitle="Reflections on life and governance in Brookline" />

      <main id="main-content" tabIndex={-1} className="notepad-content">
        <div className="notepad-list">
          {sorted.map(article => (
            <Link
              key={article.id}
              to={`/notepad/${article.id}`}
              className="notepad-card"
            >
              {article.coverImage && (
                <div className="notepad-card-image">
                  <img src={article.coverImage} alt={article.coverAlt || ''} />
                </div>
              )}
              <div className="notepad-card-body">
                <time className="notepad-card-date" dateTime={article.date}>
                  {formatDate(article.date)}
                </time>
                <h2 className="notepad-card-title">{article.title}</h2>
                <p className="notepad-card-summary">{article.summary}</p>
                <span className="notepad-card-read">Read more</span>
              </div>
            </Link>
          ))}
        </div>

        <DisclaimerFooter />
      </main>
    </div>
  );
}

function NotepadArticle() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const article = notepadArticles.find(a => a.id === articleId);

  // Only show the article's name in the tab to someone allowed to read it. The
  // title would otherwise disclose a gated article's name to anyone holding the
  // URL — the same leak the per-article link-preview cards already avoid.
  useEffect(() => {
    document.title = article && canViewArticle(article, user)
      ? `${article.title} - Brookline Notepad`
      : 'Brookline Notepad';
  }, [article, user]);

  // Gate private/member articles by the viewer's account.
  if (article && !canViewArticle(article, user)) {
    if (loading) return null;
    if (!user) return <Navigate to={authUrl('/signin', location)} replace />;
    return (
      <div className="notepad-page">
        <Header title="Brookline Notepad" />
        <main className="notepad-content">
          <div className="notepad-not-found">
            <h2>Access Denied</h2>
            <p>You don't have permission to view this article.</p>
            <Link to="/notepad">Back to all articles</Link>
          </div>
        </main>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="notepad-page">
        <Header title="Brookline Notepad" />
        <main className="notepad-content">
          <div className="notepad-not-found">
            <h2>Article not found</h2>
            <Link to="/notepad">Back to all articles</Link>
          </div>
        </main>
      </div>
    );
  }

  // Links written into an article's HTML are invisible to React Router, so a
  // same-site one would trigger a full page reload. Route those through the
  // router instead, leaving modified clicks, downloads and off-site links to
  // the browser.
  const handleArticleLinkClick = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  return (
    <div className="notepad-page">
      <a href="#main-content" className="notepad-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" subtitle="Reflections on life and governance in Brookline" />
      <main id="main-content" tabIndex={-1} className="notepad-content">
        <button className="notepad-back" onClick={() => navigate('/notepad')}>
          ← Back to all articles
        </button>

        <article className="notepad-article">
          {article.coverImage && (
            <div className="notepad-article-hero">
              <img src={article.coverImage} alt={article.coverAlt || ''} />
            </div>
          )}

          <time className="notepad-article-date" dateTime={article.date}>
            {formatDate(article.date)}
          </time>
          <h2 className="notepad-article-title">{article.title}</h2>
          <div className="notepad-article-body" onClick={handleArticleLinkClick}>
            {/* A paragraph entry that is a list renders as one: a <ul>/<ol> inside a
                <p> is invalid HTML and the browser would break it out anyway. */}
            {article.paragraphs.map((p, i) =>
              /^\s*<(ul|ol)\b/i.test(p) ? (
                <div key={i} className="notepad-article-list" dangerouslySetInnerHTML={{ __html: p }} />
              ) : (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              )
            )}
          </div>
        </article>

        {/* Sign-up prompt for signed-out readers only — someone with an account
            is already receiving these, so the block would be noise for them.
            `loading` is checked too so it does not flash before auth resolves. */}
        {!loading && !user && (
          <aside className="notepad-signup" aria-labelledby="notepad-signup-heading">
            <h2 id="notepad-signup-heading" className="notepad-signup-title">
              Enjoyed this?
            </h2>
            <p className="notepad-signup-text">
              Sign up to receive occasional reflections on life and governance in Brookline.
            </p>
            <Link to={authUrl('/signup', location)} className="notepad-signup-btn">Sign Up</Link>
          </aside>
        )}

        <DisclaimerFooter />
      </main>
    </div>
  );
}

export { NotepadList, NotepadArticle };
